/**
 * Relatórios > Financeiro > DRE > Conceitos.
 *
 * Conteúdo estático e didático (sem chamadas de API) explicando os conceitos
 * da DRE gerencial da Viver Organizer: competência vs caixa, receita por
 * notas fiscais, custo × despesa, fixo × variável, margem de contribuição,
 * EBITDA, resultado líquido e ponto de equilíbrio.
 *
 * O MESMO exemplo numérico é encadeado do início ao fim para o leitor
 * acompanhar: R$ 100 mil de receita bruta → R$ 5 mil de resultado líquido.
 */
import React from 'react';
import {
  ArrowDownUp,
  BookOpen,
  Calculator,
  Coins,
  Landmark,
  Scale,
  Target,
} from 'lucide-react';

// ─── Blocos de apresentação ─────────────────────────────────────

const Section: React.FC<{
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}> = ({ icon, title, children }) => (
  <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-3">
    <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800">
      <span className="text-emerald-600">{icon}</span>
      {title}
    </h3>
    <div className="space-y-3 text-sm leading-relaxed text-slate-600">{children}</div>
  </section>
);

const Callout: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm leading-relaxed text-emerald-900">
    {children}
  </div>
);

/** Linha de exemplo numérico (rótulo + valor formatado à direita). */
const ExampleRow: React.FC<{ label: string; value: string; bold?: boolean; negative?: boolean }> = ({
  label,
  value,
  bold,
  negative,
}) => (
  <div
    className={`flex items-center justify-between gap-4 px-4 py-2 text-sm ${
      bold ? 'font-semibold bg-slate-50 text-slate-900' : 'text-slate-700'
    }`}
  >
    <span>{label}</span>
    <span className={`whitespace-nowrap ${negative ? 'text-red-600' : ''}`}>{value}</span>
  </div>
);

const ExampleBox: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="border border-slate-200 rounded-lg overflow-hidden">
    <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500 bg-slate-50 border-b border-slate-200">
      {title}
    </p>
    <div className="divide-y divide-slate-100">{children}</div>
  </div>
);

// ─── Conteúdo ───────────────────────────────────────────────────

export const DreConceptsContent: React.FC = () => (
  <div className="space-y-6">
    <p className="text-sm text-slate-500">
      Guia rápido dos conceitos por trás do Demonstrativo. O mesmo exemplo numérico é usado do começo
      ao fim: acompanhe os R$ 100 mil de receita bruta até virarem R$ 5 mil de resultado líquido.
    </p>

    {/* 1. O que é a DRE gerencial */}
    <Section icon={<BookOpen size={18} />} title="1. O que é a DRE gerencial">
      <p>
        A DRE (Demonstração do Resultado do Exercício) gerencial mostra se a operação{' '}
        <strong>deu lucro ou prejuízo no período</strong> — independentemente de quando o dinheiro
        entrou ou saiu do banco. Ela responde &ldquo;quanto a empresa ganhou vendendo?&rdquo;, enquanto o
        Fluxo de Caixa responde &ldquo;quanto dinheiro passou pela conta?&rdquo;.
      </p>
      <p>
        A diferença está no regime de apuração:
      </p>
      <ul className="list-disc pl-5 space-y-1">
        <li>
          <strong>Regime de competência (usado na DRE):</strong> receitas e gastos entram no mês do{' '}
          <em>fato gerador</em> — quando a venda aconteceu ou o gasto foi incorrido.
        </li>
        <li>
          <strong>Regime de caixa (usado no Fluxo de Caixa):</strong> entradas e saídas entram no mês
          em que o dinheiro efetivamente se moveu na conta.
        </li>
      </ul>
      <Callout>
        <strong>Exemplo prático:</strong> a fatura do cartão de crédito paga em julho contém compras
        feitas em junho. Na DRE, essas compras são <strong>despesa de junho</strong> (competência);
        no caixa, são <strong>saída de julho</strong> (quando a fatura foi paga).
      </Callout>
      <Callout>
        <strong>Importante — de onde vem a receita desta DRE:</strong> a receita reconhecida é o{' '}
        <strong>valor das notas fiscais emitidas no período</strong> — NF-e para produtos e NFS-e para
        serviços — e <strong>não</strong> o dinheiro que entrou em caixa. Uma venda faturada em junho
        e recebida em julho é receita de junho na DRE. Clique nas linhas de receita do Demonstrativo
        para ver as notas que compõem cada valor.
      </Callout>
    </Section>

    {/* 2. Receita Bruta e Receita Líquida */}
    <Section icon={<Coins size={18} />} title="2. Receita Bruta e Receita Líquida">
      <p>
        <strong>Receita Bruta</strong> é tudo o que foi faturado: o total das notas fiscais de{' '}
        <strong>produtos</strong> (NF-e) mais as notas fiscais de <strong>serviços</strong> (NFS-e)
        emitidas no período.
      </p>
      <p>
        Só que nem tudo isso fica na empresa. As <strong>deduções sobre vendas</strong> saem antes de
        qualquer outra conta: impostos sobre a venda (DAS do Simples, ICMS) e devoluções de clientes.
        O que sobra é a <strong>Receita Líquida</strong> — a base real para todas as margens da DRE.
      </p>
      <ExampleBox title="Exemplo numérico">
        <ExampleRow label="Notas fiscais de produtos (NF-e)" value="R$ 80.000" />
        <ExampleRow label="Notas fiscais de serviços (NFS-e)" value="R$ 20.000" />
        <ExampleRow label="(=) Receita Bruta" value="R$ 100.000" bold />
        <ExampleRow label="(−) Impostos sobre venda (DAS/ICMS)" value="− R$ 8.000" negative />
        <ExampleRow label="(−) Devoluções" value="− R$ 2.000" negative />
        <ExampleRow label="(=) Receita Líquida" value="R$ 90.000" bold />
      </ExampleBox>
    </Section>

    {/* 3. Custo × Despesa */}
    <Section icon={<Scale size={18} />} title="3. Custo × Despesa">
      <p>
        <strong>Custo</strong> é o gasto diretamente ligado a <em>produzir ou entregar</em> o que foi
        vendido: se a venda não existisse, o gasto também não existiria. <strong>Despesa</strong> é o
        gasto para <em>manter a operação funcionando</em>, com ou sem venda.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="border border-slate-200 rounded-lg p-3">
          <p className="font-semibold text-slate-800 mb-1">Custos (ligados à entrega)</p>
          <ul className="list-disc pl-5 space-y-0.5">
            <li>Mercadoria vendida (CMV) dos produtos de organização</li>
            <li>Embalagens</li>
            <li>Frete de venda (ex.: Melhor Envio)</li>
            <li>Comissões e diárias de personal organizer</li>
          </ul>
        </div>
        <div className="border border-slate-200 rounded-lg p-3">
          <p className="font-semibold text-slate-800 mb-1">Despesas (manter a operação)</p>
          <ul className="list-disc pl-5 space-y-0.5">
            <li>Aluguel e condomínio</li>
            <li>Contador</li>
            <li>Sistemas e licenças de software</li>
            <li>Marketing institucional</li>
          </ul>
        </div>
      </div>
      <p>
        A separação importa porque os custos definem a <strong>margem de cada venda</strong>{' '}
        (seção 5), enquanto as despesas definem o <strong>tamanho da estrutura</strong> que essa
        margem precisa sustentar.
      </p>
    </Section>

    {/* 4. Fixo × Variável */}
    <Section icon={<ArrowDownUp size={18} />} title="4. Fixo × Variável">
      <p>
        <strong>Fixo</strong> é o gasto que não muda com o volume de vendas no curto prazo: vendendo o
        dobro ou a metade, ele continua praticamente igual (aluguel, salários, licenças).{' '}
        <strong>Variável</strong> é o gasto que cresce junto com as vendas: cada venda a mais gera
        mais frete, mais taxa de cartão, mais embalagem, mais comissão.
      </p>
      <p>Cruzando as duas classificações (custo/despesa × fixo/variável), temos 4 tipos de lançamento:</p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left py-2 px-3 font-semibold text-slate-600 w-28"> </th>
              <th className="text-left py-2 px-3 font-semibold text-slate-600">Variável</th>
              <th className="text-left py-2 px-3 font-semibold text-slate-600">Fixo</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-slate-100 align-top">
              <td className="py-2 px-3 font-semibold text-slate-800">Custo</td>
              <td className="py-2 px-3 text-slate-600">
                <strong>Custo Variável:</strong> CMV, frete Melhor Envio, embalagens, comissão de
                personal organizer. Cresce a cada venda entregue.
              </td>
              <td className="py-2 px-3 text-slate-600">
                <strong>Custo Fixo:</strong> estrutura diretamente ligada à entrega que se mantém
                constante (ex.: espaço/equipe dedicados à operação de entrega).
              </td>
            </tr>
            <tr className="align-top">
              <td className="py-2 px-3 font-semibold text-slate-800">Despesa</td>
              <td className="py-2 px-3 text-slate-600">
                <strong>Despesa Variável:</strong> marketing de performance (tráfego pago por
                resultado), viagens comerciais. Acompanha o esforço de vender mais.
              </td>
              <td className="py-2 px-3 text-slate-600">
                <strong>Despesa Fixa:</strong> aluguel, contador, telefonia, tarifas bancárias. Existe
                todo mês, vendendo ou não.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        <strong>Por que a classificação importa:</strong> os gastos <em>variáveis</em> definem quanto
        sobra de cada venda (margem de contribuição); os gastos <em>fixos</em> definem o faturamento
        mínimo para não ter prejuízo (ponto de equilíbrio). Um lançamento classificado errado distorce
        os dois indicadores — por exemplo, tratar comissão de PO como despesa fixa faria a margem
        parecer maior do que é e o ponto de equilíbrio parecer menor do que é.
      </p>
    </Section>

    {/* 5. Margem de Contribuição */}
    <Section icon={<Calculator size={18} />} title="5. Margem de Contribuição">
      <p>
        <strong>Margem de Contribuição = Receita Líquida − Custos Variáveis.</strong> É o que sobra de
        cada venda para pagar a estrutura fixa (e, depois dela, gerar lucro). Se a margem de
        contribuição é positiva, cada venda adicional ajuda; se é negativa, quanto mais se vende, mais
        se perde.
      </p>
      <ExampleBox title="Exemplo numérico (continuando)">
        <ExampleRow label="Receita Líquida" value="R$ 90.000" bold />
        <ExampleRow label="(−) CMV (mercadorias)" value="− R$ 30.000" negative />
        <ExampleRow label="(−) Embalagens" value="− R$ 2.000" negative />
        <ExampleRow label="(−) Frete de venda" value="− R$ 5.000" negative />
        <ExampleRow label="(−) Comissões e diárias de PO" value="− R$ 8.000" negative />
        <ExampleRow label="(=) Margem de Contribuição" value="R$ 45.000" bold />
      </ExampleBox>
      <Callout>
        Margem de contribuição de <strong>R$ 45.000 sobre R$ 90.000 de receita líquida = 50%</strong>:
        a cada R$ 100 vendidos (líquidos), R$ 50 sobram para pagar a estrutura fixa.
      </Callout>
    </Section>

    {/* 6. EBITDA e Resultado Líquido */}
    <Section icon={<Landmark size={18} />} title="6. EBITDA e Resultado Líquido">
      <p>
        Da margem de contribuição, descontamos toda a estrutura — custos fixos, despesas fixas,
        pessoal, despesas variáveis e marketing — e chegamos ao <strong>EBITDA</strong>: o resultado
        operacional do negócio, antes de juros e efeitos financeiros. Somando o{' '}
        <strong>resultado financeiro</strong> (rendimentos menos juros e tarifas), chegamos ao{' '}
        <strong>Resultado Líquido</strong> do período.
      </p>
      <ExampleBox title="Exemplo numérico (fechando a conta)">
        <ExampleRow label="Margem de Contribuição" value="R$ 45.000" bold />
        <ExampleRow label="(−) Custos fixos" value="− R$ 5.000" negative />
        <ExampleRow label="(−) Despesas fixas (aluguel, contador, sistemas)" value="− R$ 12.000" negative />
        <ExampleRow label="(−) Pessoal" value="− R$ 15.000" negative />
        <ExampleRow label="(−) Despesas variáveis" value="− R$ 3.000" negative />
        <ExampleRow label="(−) Marketing" value="− R$ 4.000" negative />
        <ExampleRow label="(=) EBITDA" value="R$ 6.000" bold />
        <ExampleRow label="(+) Resultado financeiro (juros e tarifas)" value="− R$ 1.000" negative />
        <ExampleRow label="(=) Resultado Líquido" value="R$ 5.000" bold />
      </ExampleBox>
      <Callout>
        Dos R$ 100.000 faturados, sobraram <strong>R$ 5.000 (margem líquida de 5,6%</strong> sobre a
        receita líquida de R$ 90.000<strong>)</strong>. A margem EBITDA foi de{' '}
        <strong>6,7%</strong> (R$ 6.000 / R$ 90.000).
      </Callout>
    </Section>

    {/* 7. Ponto de equilíbrio e indicadores */}
    <Section icon={<Target size={18} />} title="7. Ponto de equilíbrio e indicadores">
      <p>
        O <strong>ponto de equilíbrio</strong> é o faturamento mínimo do mês para não ter prejuízo:
        gastos fixos ÷ % de margem de contribuição. No exemplo, R$ 39.000 de estrutura ÷ 50% de margem
        = <strong>R$ 78.000</strong> de receita líquida — abaixo disso o mês fecha no vermelho.
      </p>
      <p>Os cards no topo do Demonstrativo trazem esses indicadores calculados para o período:</p>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Margem de Contribuição %</strong> — quanto sobra de cada venda (seção 5).</li>
        <li><strong>Margem EBITDA %</strong> e <strong>Margem Líquida %</strong> — eficiência operacional e resultado final (seção 6).</li>
        <li><strong>Ponto de Equilíbrio</strong> — faturamento mínimo do período.</li>
        <li><strong>Cobertura Custo Fixo</strong> — quantas vezes a margem de contribuição paga a estrutura fixa (acima de 1× a operação é superavitária).</li>
        <li><strong>Mix Produto × Serviço</strong> — participação de cada frente na receita.</li>
      </ul>
    </Section>
  </div>
);

export default DreConceptsContent;
